<template>
  <v-card>
    <v-toolbar :dark="$vuetify.theme.dark" color="primary">
      <v-toolbar-title>
        {{ $t('Выбор победителя для торговой процедуры № {0}', [ lot.lotNumber ]) }}
      </v-toolbar-title>
    </v-toolbar>
    <general-error v-if="!!generalError" />
    <v-alert
        class="text-center"
        type="success"
        :value="success">
      {{ $t('Изменения сохранены') }}
    </v-alert>
    <v-card-text>
      <v-data-table
          :headers="headers1"
          :items="orders"
          :items-per-page="itemsPerPage"
          item-key="orderId"
          class="elevation-1 orders"
          disable-sort-by
          single-expand
          show-expand
          :loading="isLoading"
          :locale="systemLocale">
        <template v-slot:progress>
          <v-progress-linear :active="isLoading" color="loader" indeterminate />
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.sessionNumber="{ item }">
          {{ item.sessionNumber }}
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.organizationName="{ item }">
          <route-link-with-tip
              :text="item.organizationName"
              :tip="$t('Просмотр организации')"
              :to="{ path: `${urlRootPath}/organization/${item.organizationId}` }" />
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.userName="{ item }">
          <route-link-with-tip
              :text="item.userName"
              :tip="$t('Просмотр пользователя')"
              :to="{ path: `${urlRootPath}/user/${item.userId}` }" />
        </template>

        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.orderDate="{ item }">
          {{ item.orderDate
            ? $moment(item.orderDate).format('DD.MM.YYYY HH:mm:ss')
            : null }}
        </template>

        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.isWinner="{ item }">
          <v-checkbox
              v-model="item.isWinner"
              v-test="{ checkbox: `isWinner_${item.orderId}` }"
              dense
              :height="10"
              :readonly="!canEdit" />
        </template>
        <template v-slot:expanded-item="{ headers, item } ">
          <td :colspan="headers.length">
            <v-row class="py-2">
              <v-col :cols="item.additionalInfo || item.fileId ? 7 : 12">
                <v-row class="my-2">
                  <text-view
                      v-if="item.deliveryBasisDescription"
                      align-self="center"
                      :label="$t('Порт/место назначения')"
                      :display="item.deliveryBasisDescription" />
                </v-row>
                <v-divider />
                <v-data-table
                    :key="item.orderId"
                    :headers="headers2"
                    :items="item.premiums || []"
                    :items-per-page="nestedItemsPerPage"
                    item-key="positionId"
                    class="premiums"
                    disable-sort-by
                    dense
                    hide-default-footer
                    :locale="systemLocale">
                  <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
                  <template v-slot:item.positionDescription="{ item }">
                    {{ item.positionDescription }}
                  </template>
                  <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
                  <template v-slot:item.premium="{ item }">
                    {{ item.premium }}
                  </template>
                  <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
                  <template v-slot:item.premiumWithDelivery="{ item }">
                    {{ item.premiumWithDelivery }}
                  </template>
                </v-data-table>
              </v-col>
              <v-col v-if="item.additionalInfo || item.fileId" cols="5">
                <v-row class="px-2 py-2 pb-0 mb-0">
                  <v-col class="pb-0 mb-0">
                    <v-icon>
                      link
                    </v-icon>
                    {{ $t('Дополнительно') }}
                  </v-col>
                </v-row>
                <v-row v-if="item.additionalInfo" class="px-2 pt-2 pb-0 mb-0">
                  <v-col class="pb-0 mb-0">
                    <v-textarea
                        :value="item.additionalInfo"
                        readonly
                        solo
                        rows="2"
                        dense />
                  </v-col>
                </v-row>
                <v-row
                    v-if="item.fileId"
                    class="px-2 pb-2 pt-0 mt-0"
                    align="center">
                  <text-view
                      :cols="10"
                      align-self="center"
                      :label="$t('Прикрепленные документы')"
                      :display="' '" />
                  <v-col cols="2" align-self="center">
                    <btn-icon-tip
                        icon="get_app"
                        :tip="$t('Скачать документ')"
                        @click="getBusinessFile(item.fileId)" />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </td>
        </template>
      </v-data-table>

      <v-row>
        <v-col class="text-left" cols="12" md="6">
          <ValidationObserver ref="tradeResultType">
            <validatable-enum-combobox
                v-model="result.tradeResultType"
                v-test="{ combobox: 'tradeResultType' }"
                rules="required"
                :values-enum="tradeResultTypesEnum"
                :label="$t('Итог торгов')"
                :readonly="!canEdit" />
          </ValidationObserver>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions v-if="canEditResults">
      <v-btn
          id="toLotBtn"
          v-test="{ btn: 'toLot' }"
          class="ma-2"
          color="primary"
          @click.stop="goToLot">
        {{ $t('К торговой процедуре') }}
      </v-btn>
      <winner-selection-dialog
          v-if="hasOrder"
          :result="result"
          :has-result="hasResult"
          :is-fix-premium="isFixPremium"
          :loading="isLoading"
          :show-delivery-basises="showDeliveryBasises"
          :save-results="saveResults" />
    </v-card-actions>
  </v-card>
</template>

<style scoped>
  .orders::v-deep td:nth-child(3) {
   word-break: break-all;
   max-width: 450px;
  }
  .orders::v-deep td:nth-child(1) {
    min-width: 200px;
  }
  .premiums::v-deep td:nth-child(1) {
    word-break: break-all;
    max-width: 550px;
  }
  .premiums::v-deep td:nth-child(2) {
    min-width: 150px;
  }
</style>

<script>
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import WinnerSelectionDialog from './WinnerSelectionDialog.vue'
  import ValidatableEnumCombobox from '@/components/reused/ui/ValidatableEnumCombobox.vue'
  import RouteLinkWithTip from '@/components/reused/ui/RouteLinkWithTip.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import {
    TRADE_RESULT_TYPE_EDIT_ENUM
  } from '@/constants/enums'
  import { LOT_STATUSES, LOT_TABS, TRADE_RESULT_TYPES, PREMIUM_TYPES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { LOT_MANAGEMENT, LOT_WINNERS_MANAGEMENT } from '@/constants/store.js'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'

  export default {
    components: {
      GeneralError,
      WinnerSelectionDialog,
      ValidatableEnumCombobox,
      RouteLinkWithTip,
      BtnIconTip,
      TextView
    },
    mixins: [
      getFileMixin('isLoading')
    ],
    props: {
      lotId: {
        type: String,
        required: true
      },
      lotType: {
        type: String,
        required: true
      }
    },
    data () {
      return {
        TRADE_RESULT_TYPES,
        isLoading: false,
        success: false,
        itemsPerPage: 5,
        nestedItemsPerPage: 5,
        result: {
          orders: [],
          tradeResultType: null
        }
      }
    },
    computed: {
      ...mapState(LOT_WINNERS_MANAGEMENT.NAMESPACE, {
        winnersInfo: state => (state[LOT_WINNERS_MANAGEMENT.STATE.WINNER_SELECTION_LIST] || {})
      }),
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        lot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT] || {})
      }),
      tradeResultTypesEnum () {
        return TRADE_RESULT_TYPE_EDIT_ENUM
      },
      headers1 () {
        let headers = [
          { text: this.$t('№ Сессии'), sortable: false, value: 'sessionNumber' },
          { text: this.$t('Организация'), sortable: false, value: 'organizationName' },
          { text: this.$t('Пользователь'), sortable: false, value: 'userName' },
          { text: this.$t('Время выставления'), sortable: false, value: 'orderDate' },
          { text: this.$t('Победитель'), sortable: false, value: 'isWinner' },
          { text: '', value: 'data-table-expand' }
        ]
        if (!this.isAdmin) {
          headers = headers.filter(h => h.value !== 'userName')
        }
        return headers
      },
      headers2 () {
        return [
          { text: this.$t('Наименование позиции'), sortable: false, value: 'positionDescription' },
          { text: this.premiumHeader, sortable: false, value: this.showDeliveryBasises ? 'premiumWithDelivery' : 'premium' }
        ]
      },
      showDeliveryBasises () {
        return this.lot.hasBasises
      },
      premiumHeader () {
        const premiumLabel = this.showDeliveryBasises
          ? this.isFixPremium
            ? 'Значение цены в базисе поставки'
            : 'Значение дифференциала в базисе поставки'
          : this.isFixPremium
            ? 'Цена за единицу в валюте Контракта'
            : 'Значение дифференциала'

        return this.$t(premiumLabel)
      },
      isFixPremium () {
        return this.lot.premiumType === PREMIUM_TYPES.FIX
      },
      orders () {
        const orders = this.winnersInfo.orders || []
        // меняем только копию из стора
        return orders.map(o => ({ ...o }))
      },
      winnerOrder () {
        return this.result.orders.length
          ? this.result.orders[0] || {}
          : {}
      },
      hasResult () {
        const result = this.winnersInfo.tradeResultType
        return !!result && result !== TRADE_RESULT_TYPES.UNSET
      },
      hasOrder () {
        return this.orders.length > 0
      },
      canEditResults () {
        return this.isLotOwner(this.lot) || this.isAdmin
      },
      canEdit () {
        return this.hasOrder &&
          this.canEditResults &&
          (this.lot.lotStatus === LOT_STATUSES.REGISTERED || this.lot.lotStatus === LOT_STATUSES.FINISHED)
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (vm.lotId) {
          vm.initialize()
        }
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_WINNERS_MANAGEMENT.NAMESPACE, {
        getWinnerSelectionListAction: LOT_WINNERS_MANAGEMENT.ACTIONS.GET_WINNER_SELECTION_LIST,
        selectWinnersAction: LOT_WINNERS_MANAGEMENT.ACTIONS.SELECT_WINNERS,
        clearWinnerSelectionListAction: LOT_WINNERS_MANAGEMENT.ACTIONS.CLEAR_WINNER_SELECTION_LIST
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotInfoAction(),
            this.clearWinnerSelectionListAction()
          ])
      },
      initialize: async function () {
        try {
          this.isLoading = true

          const model = {
            lotId: this.lotId,
            lotType: this.lotType
          }
          await this.getLotInfoAction(model)
          await this.getWinnerSelectionListAction(model)

          this.result.orders = this.orders
          this.result.tradeResultType = this.winnersInfo.tradeResultType || TRADE_RESULT_TYPES.UNSET
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      saveResults: async function () {
        const winnerOrder = this.result.orders.find(w => w.isWinner)

        const model = {
          lotId: this.lotId,
          lotType: this.lotType,
          result: {
            orderId: winnerOrder?.orderId,
            tradeResultType: this.result.tradeResultType
          }
        }
        this.success = false
        try {
          this.isLoading = true
          await this.selectWinnersAction(model)
          this.isLoading = false
          this.success = true
          await this.getWinnerSelectionListAction({
            lotId: this.lotId,
            lotType: this.lotType
          })
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      goToLot: function () {
        this.goToRoute(`${this.urlRootPath}/lot/${this.lotType}/${this.lotId}/${LOT_TABS.TRADE_RESULTS}`)
      }
    }
  }
</script>
